import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function WhitewaterRafting() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    { question: 'Is whitewater rafting safe for beginners?', answer: 'Absolutely! Over 90% of rafters are first-timers who complete the trip successfully. All operators are certified and follow international safety standards. You\'ll receive comprehensive training, professional guides will be with you throughout, and safety kayakers accompany every trip. The warm, deep water minimizes risks even if you fall out (which is part of the fun!).' },
    { question: 'Do I need to know how to swim?', answer: 'You should be able to swim at least 25 meters and be comfortable in water. If you\'re a weak swimmer, inform your guide—they can position you more securely in the raft. Life jackets are mandatory and keep you afloat even if you can\'t swim, but basic swimming ability is required for safety. Non-swimmers are generally not advised to raft Grade 5 rapids.' },
    { question: 'What are the chances of flipping?', answer: 'Grade 5 rapids have an 80%+ flip rate, so there\'s a very good chance your raft will flip at some point during the day. This is considered part of the adventure! If you flip, your life jacket keeps you safe, and safety kayakers immediately assist. Many rafts complete the entire day without flipping, but you should expect to swim at least once. It\'s exhilarating!' },
    { question: 'What is the minimum age for rafting?', answer: 'The minimum age for full-day Grade 5 rafting is 15 years (some operators require 16 for extreme Grade 5 sections). For family-friendly floats on Grade 2-3 sections, children as young as 8 years can participate. Participants under 18 require guardian consent.' },
    { question: 'Can I bring my camera or phone?', answer: 'We strongly recommend NOT bringing phones or regular cameras—they will get wet, damaged, or lost in the rapids. GoPros with secure chest mounts are acceptable at your own risk. Professional photographers capture high-quality action shots and video of your entire trip from kayaks and shore, available for purchase after. This is the best way to get amazing footage without risk.' },
    { question: 'What if I want to skip a rapid?', answer: 'You can walk around any rapid you\'re not comfortable with—guides will point out the portage route. There\'s no shame in sitting one out! Safety boats are also available if you want to discontinue rafting at any point. Your safety and comfort are the top priorities.' },
    { question: 'When is the best time to go rafting?', answer: 'Rafting is available year-round thanks to Lake Victoria\'s flow regulation, which maintains consistent water levels. The dry seasons (December-February and June-September) offer the most favorable weather with less rain, but rafting during the wet season is equally thrilling with slightly higher water levels. Book any time!' },
    { question: 'Is lunch included?', answer: 'Yes! Full-day rafting trips include a delicious Ugandan buffet lunch served at a scenic river island location. Vegetarian and dietary requirements can be accommodated with advance notice. Drinking water is provided throughout the day.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Whitewater Rafting the Nile
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Conquer legendary Grade 5 rapids at the source of the world's longest river
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(1,200+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                International Safety Standards
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-award" className="w-4 h-4 mr-2" />
                World-Class Rapids
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Beginners Welcome
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Rafting Adventure
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Africa's Ultimate Whitewater Adventure
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Whitewater rafting on the Nile River delivers an adrenaline-pumping experience as rafts plunge down massive Grade 5 rapids, with water crashing over the bow while teams paddle furiously through white foam. Guides shout commands over the roar of the Nile, and moments of weightlessness occur between waves before hitting the bottom of wave trains. Participants emerge soaked, exhilarated, and ready for the next rapid on this legendary river.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Jinja, Uganda—the adventure capital of East Africa—sits at the source of the Nile, where the world's longest river begins its 6,650-kilometer journey to the Mediterranean. Just downstream from this historic point lies a 20-26 kilometer stretch of world-class whitewater that attracts adrenaline seekers from around the globe. With warm water year-round (23°C), consistent flows regulated by Lake Victoria, and rapids ranging from gentle Grade 2-3 warm-ups to heart-stopping Grade 5 monsters, this is bucket-list rafting at its absolute finest.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Nile's power and volume create rapids that many experts consider superior to other famous rafting destinations like the Zambezi. While the construction of the Bujagali and Isimba dams affected some historic rapids like Namizi and Kyabirwa, rafting companies have expertly adjusted routes to maintain the thrill. Today's full-day adventure tackles approximately 8-10 major rapids, with four being Grade 5, interspersed with calm sections perfect for swimming, optional cliff jumping, and soaking in the incredible tropical scenery.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">The Rapids That Made Jinja Famous</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Each rapid on the Nile has its own personality and legendary status among rafters. <strong>Overtime</strong>, the grand finale, features massive drops and chaotic waves that flip rafts with gleeful regularity. <strong>The Bad Place</strong> lives up to its ominous name with powerful hydraulics and unpredictable currents. <strong>Vengeance</strong>, one of the longest Grade 4 rapids, provides sustained excitement as you navigate through complex wave trains. <strong>Hair of the Dog</strong> and <strong>Big Brother</strong> test your teamwork with technical lines and powerful holes. <strong>Silverback</strong> and <strong>Nile Special</strong> add variety to the day's challenges.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Grade 5 rapids have an 80%+ flip rate, meaning there's a very good chance you'll end up swimming at some point. This isn't a bug—it's a feature! Getting tossed into the warm Nile, bobbing through rapids in your life jacket, and being rescued by safety kayakers is all part of the unforgettable experience. Between the big rapids, you'll float through calm sections where lush tropical islands, rocky outcrops, and abundant birdlife create a stunning backdrop. Vervet monkeys watch from the trees, and African fish eagles soar overhead.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Safety: Professional Standards, Thrilling Experience</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Whitewater rafting is an adventure sport, but it's remarkably safe when conducted by certified operators adhering to international standards. All rafting companies in Jinja employ experienced guides trained in swift water rescue and international rafting protocols. Before launching, you'll receive a comprehensive safety briefing covering paddling technique, swimming in rapids, what to do if you fall out, proper use of safety gear, and communication signals.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  You'll be equipped with a properly fitted life jacket and helmet, both mandatory throughout the trip. Multiple safety kayakers accompany every rafting group, ready to assist if anyone falls out or if a raft flips. Safety boats are available for participants who wish to discontinue the activity at any point. The Nile's deep waters minimize the risk of hitting rocks, even in the most turbulent rapids. Over 90% of rafters are first-timers who complete the trip successfully and safely—no prior experience is required, just reasonable fitness and a sense of adventure.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Raft the Nile in Jinja
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-water', title: 'Legendary Grade 5 Rapids', description: 'Tackle 8-10 major rapids including iconic Grade 5 monsters like Overtime, The Bad Place, and Big Brother' },
                  { icon: 'fa-sun', title: 'Warm Water Year-Round', description: 'Raft in tropical 23°C water—no wetsuits needed! Comfortable swimming conditions even when you flip' },
                  { icon: 'fa-shield-halved', title: 'International Safety Standards', description: 'Certified operators, experienced guides, comprehensive briefings, and safety kayakers ensure your safety' },
                  { icon: 'fa-users', title: 'Perfect for First-Timers', description: 'Over 90% of rafters are beginners. Full training provided—just bring your sense of adventure!' },
                  { icon: 'fa-camera', title: 'Professional Photo/Video', description: 'Epic action shots and video of your raft conquering massive waves, available for purchase' },
                  { icon: 'fa-utensils', title: 'Riverside Buffet Lunch', description: 'Delicious Ugandan buffet served at a beautiful river island location mid-adventure' },
                  { icon: 'fa-person-swimming', title: 'Swimming & Cliff Jumping', description: 'Cool off in calm sections and take optional jumps from riverside cliffs between rapids' },
                  { icon: 'fa-tree', title: 'Stunning Natural Beauty', description: 'Float past lush tropical islands, spot vervet monkeys, and watch African fish eagles soar' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Full-Day Rafting Adventure Timeline
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be fully customized based on your preferences
                  and pickup location. We can adjust start times, combine with other Jinja activities, or tailor the experience to your group.
                  Just let us know what works best for you!
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '07:30', title: 'Pickup & Transfer to Base', desc: 'Pickup from your Jinja accommodation (or Kampala with early start). Transfer to the rafting base where you\'ll enjoy coffee, tea, and light snacks while meeting your team.' },
                  { time: '08:30', title: 'Safety Briefing & Equipment', desc: 'Comprehensive safety briefing covering paddling commands, swimming in rapids, and rescue procedures. Get fitted with helmet and life jacket, then practice paddling on land.' },
                  { time: '09:30', title: 'Launch & Warm-Up Rapids', desc: 'Launch into the Nile and tackle gentle Grade 2-3 rapids to practice teamwork, paddling technique, and build confidence before the big ones.' },
                  { time: '10:30', title: 'The Big Grade 5 Rapids', desc: 'Tackle legendary Grade 5 monsters like Hair of the Dog, Big Brother, and The Bad Place. Adrenaline time! Expect massive waves, potential flips, and pure excitement.' },
                  { time: '12:30', title: 'Riverside Island Lunch', desc: 'Stop at a beautiful river island for a delicious Ugandan buffet lunch. Relax, swim, and enjoy optional cliff jumping (various heights available).' },
                  { time: '14:00', title: 'Afternoon Rapids & Finale', desc: 'Continue downstream through more Grade 4-5 rapids including Nile Special and Silverback, building to the grand finale: Overtime, the ultimate Grade 5 challenge!' },
                  { time: '16:00', title: 'Return, Showers & Celebration', desc: 'Return to base for hot showers, cold drinks, and viewing your professional photos/videos. Receive your rafting certificate and transfer back to accommodation.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring for Your Rafting Day
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-person-swimming" className="w-6 h-6" />
                    Essential Clothing
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Swimsuit or boardshorts (you'll be wet all day)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Quick-dry shirt or rash guard (sun protection)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sports sandals or old sneakers (secure footwear)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Complete change of dry clothes for after</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-sun" className="w-6 h-6" />
                    Sun Protection & Extras
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof sunscreen (SPF 50+ recommended)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sunglasses with secure strap (optional)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Towel for after the trip</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Cash for tips and photo/video purchase</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 p-6 bg-white rounded-lg border-l-4 border-safari-gold shadow-sm">
                <p className="text-gray-700">
                  <strong className="text-safari-green">Important:</strong> Do NOT bring phones, regular cameras, or valuables on the raft—they will get wet or lost. GoPros with secure chest mounts are acceptable at your own risk. Professional photographers capture high-quality photos and video of your entire trip, available for purchase after. Lockers are provided at the base for your belongings.
                </p>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Frequently Asked Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Jinja Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/bungee-jumping" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1528543606781-2f6e6857f318?w=800"
                      alt="Bungee Jumping"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Bungee Jumping Over the Nile</h3>
                    <p className="text-gray-700 mb-4">44-meter plunge over the legendary Nile. The ultimate adrenaline rush!</p>
                    <p className="text-safari-gold font-bold">Activity fees apply – contact us for pricing</p>
                  </div>
                </Link>

                <Link to="/activities/kayaking-adventures" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1484876065684-b683cf17d276?w=800"
                      alt="Kayaking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kayaking Adventures</h3>
                    <p className="text-gray-700 mb-4">Paddle the Nile at your own pace, from calm floats to whitewater challenges.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply – contact us for pricing</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> Full Day (6-8 hours)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate to Challenging</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group Size:</strong> 6-8 per raft</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 15 years (Grade 5)</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Rafting Adventure
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Professional river guide",
                    "All safety equipment (helmet, life jacket)",
                    "Safety kayakers throughout",
                    "Riverside buffet lunch",
                    "Drinking water and snacks",
                    "Transfers from Jinja town",
                    "Comprehensive safety briefing",
                    "Rafting certificate",
                    "Hot showers after trip",
                    "Secure lockers for belongings"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Professional photos/videos (available for purchase)",
                    "Transfers from Kampala (can be arranged)",
                    "Accommodation before/after",
                    "Tips for guides and crew",
                    "Alcoholic beverages",
                    "Travel insurance (highly recommended)",
                    "Personal expenses"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness Requirements
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Swimming:</strong> Must be able to swim at least 25 meters and be comfortable in water.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate fitness required. Paddling is physically demanding but manageable.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> Not suitable for pregnant women or those with heart conditions, back problems, or recent injuries.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Sobriety:</strong> Participants must be sober. No alcohol or drugs before rafting.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Whitewater Rafting the Nile - Jinja"
      />
    </div>
  )
}
